import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  CurrencyDollar, DownloadSimple, ArrowClockwise, CaretRight, CaretDown,
  Truck, User as UserIcon, WarningCircle, Calendar, ArrowLeft, ArrowRight, Wallet,
  PaperPlaneTilt, FilePdf, EnvelopeSimple, CheckCircle,
} from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import PayrollQboSyncBar from "../components/PayrollQboSyncBar";

const EMPLOYMENT_LABEL = {
  owner_op: "Owner Op",
  company: "Company",
  owner: "Owner",
};

const PAY_METHOD_LABEL = {
  pct_gross: "% of Gross",
  pct_net_after_fuel: "% of Net (after fuel)",
  per_mile: "$ per Mile",
  salary: "Salary (flat)",
};

const DEDUCTION_META = [
  { key: "fuel_advances", label: "Fuel Advances" },
  { key: "insurance", label: "Insurance" },
  { key: "escrow", label: "Escrow" },
  { key: "chargebacks", label: "Chargebacks" },
  { key: "truck_lease", label: "Truck Lease" },
  { key: "trailer_rental", label: "Trailer Rental" },
  { key: "other", label: "Other" },
];

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Convert JS Date → YYYY-MM-DD (UTC)
function iso(d) {
  return d.toISOString().split("T")[0];
}

// Get Monday of current week (or use payDay to align)
function currentWeekStart(payDay = "friday") {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri
  const start = new Date(now);
  if (payDay === "monday") {
    // Week runs Mon-Sun, paid on Monday
    const daysBack = day === 0 ? 6 : day - 1;
    start.setDate(now.getDate() - daysBack);
  } else {
    // Week runs Sat-Fri, paid on Friday
    const daysBack = (day + 1) % 7; // Fri=0, Thu=6... hmm let me redo
    // Simpler: last completed Friday
    const daysBack2 = (day - 5 + 7) % 7 || 7;
    start.setDate(now.getDate() - daysBack2 - 6);
  }
  return start;
}

function DriverRow({ line, onUpdateDeduction, onSaveAdjustments, expanded, onToggle,
                    paystubStatus, onPreviewPaystub, onSendPaystub }) {
  const [saving, setSaving] = useState(false);
  const [sendingPaystub, setSendingPaystub] = useState(false);
  const d = line.driver;
  const isNet = line.net_pay >= 0;

  const save = async () => {
    setSaving(true);
    try {
      await onSaveAdjustments(line);
      toast.success(`Saved deductions for ${d.name}`);
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border-b border-white/5" data-testid={`payroll-row-${d.id}`}>
      <button
        onClick={onToggle}
        data-testid={`payroll-expander-${d.id}`}
        className="w-full grid grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-white/[0.02] text-left transition-colors"
      >
        <div className="col-span-4 flex items-center gap-2">
          {expanded ? <CaretDown size={14} className="text-neutral-400" /> : <CaretRight size={14} className="text-neutral-500" />}
          <div>
            <div className="text-sm font-medium text-white">{d.name || "—"}</div>
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <span className={
                d.employment_type === "owner_op" ? "text-purple-400" :
                d.employment_type === "owner" ? "text-emerald-400" : "text-blue-400"
              }>
                {EMPLOYMENT_LABEL[d.employment_type] || "Company"}
              </span>
              <span className="text-neutral-700">·</span>
              <span>{PAY_METHOD_LABEL[d.pay_method] || "—"}</span>
              <span className="text-neutral-700">·</span>
              <span>
                {d.pay_method === "per_mile" ? `$${Number(d.pay_rate).toFixed(2)}/mi` :
                 d.pay_method === "salary" ? `${money(d.pay_rate)}/wk` :
                 `${d.pay_rate}%`}
              </span>
            </div>
          </div>
        </div>
        <div className="col-span-1 text-xs text-neutral-400 tabular-nums text-right">{line.load_count}</div>
        <div className="col-span-2 text-xs text-neutral-300 tabular-nums text-right">{line.miles.toLocaleString()} mi</div>
        <div className="col-span-2 text-xs text-neutral-300 tabular-nums text-right">{money(line.revenue)}</div>
        <div className="col-span-1 text-xs text-white font-semibold tabular-nums text-right">{money(line.gross_pay)}</div>
        <div className="col-span-1 text-xs text-red-400 tabular-nums text-right">−{money(line.deductions_total)}</div>
        <div className={`col-span-1 text-sm font-semibold tabular-nums text-right flex items-center justify-end gap-1 ${isNet ? "text-emerald-400" : "text-red-400"}`}
             data-testid={`payroll-net-${d.id}`}>
          {money(line.net_pay)}
          {paystubStatus === "sent" && (
            <CheckCircle size={12} weight="fill" className="text-emerald-500"
                         data-testid={`payroll-paystub-sent-${d.id}`}
                         title="Paystub sent" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-5 pt-2 bg-black/30" data-testid={`payroll-detail-${d.id}`}>
          {/* Loads list */}
          {line.loads.length > 0 ? (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Loads in period ({line.loads.length})</div>
              <div className="rounded-md border border-white/5 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.02] text-neutral-500">
                    <tr>
                      <th className="text-left p-2">Load #</th>
                      <th className="text-left p-2">Delivered</th>
                      <th className="text-left p-2">Origin → Destination</th>
                      <th className="text-right p-2">Miles</th>
                      <th className="text-right p-2">Rate</th>
                      <th className="text-right p-2">Fuel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {line.loads.map((ld) => (
                      <tr key={ld.id} className="border-t border-white/5">
                        <td className="p-2 text-neutral-300 font-mono">{ld.load_number}</td>
                        <td className="p-2 text-neutral-500">{ld.delivery_date || "—"}</td>
                        <td className="p-2 text-neutral-400 truncate max-w-xs">{ld.origin} → {ld.destination}</td>
                        <td className="p-2 text-right text-neutral-400 tabular-nums">{ld.miles || 0}</td>
                        <td className="p-2 text-right text-white tabular-nums">{money(ld.rate)}</td>
                        <td className="p-2 text-right text-orange-400 tabular-nums">{ld.fuel_cost ? money(ld.fuel_cost) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-xs text-neutral-500 mb-4">
              <WarningCircle size={12} weight="fill" className="inline text-yellow-500 mr-1" />
              No delivered loads for this driver in the period.
              {d.pay_method === "salary" && <span> (Salary driver — still paid.)</span>}
            </div>
          )}

          {/* Deductions grid */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Deductions</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {DEDUCTION_META.map((m) => (
                <label key={m.key} className="block">
                  <div className="text-[11px] text-neutral-500 mb-1">{m.label}</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.deductions[m.key] || 0}
                    onChange={(e) => onUpdateDeduction(d.id, m.key, parseFloat(e.target.value) || 0)}
                    data-testid={`payroll-deduction-${d.id}-${m.key}`}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5 text-sm text-white tabular-nums"
                  />
                </label>
              ))}
              <label className="block">
                <div className="text-[11px] text-neutral-500 mb-1">Other Note</div>
                <input
                  type="text"
                  value={line.other_note || ""}
                  onChange={(e) => onUpdateDeduction(d.id, "other_note", e.target.value)}
                  placeholder="e.g. broken mirror"
                  data-testid={`payroll-deduction-${d.id}-other_note`}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                />
              </label>
            </div>

            <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
              <div className="text-xs">
                <span className="text-neutral-500">Total Deductions: </span>
                <span className="text-red-400 tabular-nums font-semibold">−{money(line.deductions_total)}</span>
                <span className="text-neutral-500 mx-2">·</span>
                <span className="text-neutral-500">Net Pay: </span>
                <span className={`tabular-nums font-semibold ${isNet ? "text-emerald-400" : "text-red-400"}`}>
                  {money(line.net_pay)}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onPreviewPaystub(d.id)}
                  data-testid={`payroll-preview-paystub-${d.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-300 hover:text-white hover:border-white/25 inline-flex items-center gap-1 transition-colors"
                >
                  <FilePdf size={12} weight="fill" /> Preview PDF
                </button>
                <button
                  onClick={async () => { setSendingPaystub(true); try { await onSendPaystub(d.id); } finally { setSendingPaystub(false); } }}
                  disabled={sendingPaystub || !d.email_present}
                  data-testid={`payroll-send-paystub-${d.id}`}
                  title={d.email_present ? "Email paystub PDF to driver" : "Driver has no email on file"}
                  className="text-xs rounded-full px-3 py-1.5 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 inline-flex items-center gap-1 transition-colors"
                >
                  <PaperPlaneTilt size={12} weight="fill" /> {sendingPaystub ? "Sending..." : "Send Paystub"}
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid={`payroll-save-${d.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-[#FF5A1F]/40 text-[#FF5A1F] hover:bg-[#FF5A1F]/10 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save deductions"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PayrollPage() {
  const { user } = useAuth();
  const [payDay, setPayDay] = useState(() => localStorage.getItem("fai_pay_day") || "friday");
  const [periodStart, setPeriodStart] = useState(() => iso(currentWeekStart(localStorage.getItem("fai_pay_day") || "friday")));
  const [periodEnd, setPeriodEnd] = useState(() => {
    const s = currentWeekStart(localStorage.getItem("fai_pay_day") || "friday");
    const e = new Date(s); e.setDate(e.getDate() + 6);
    return iso(e);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(false);
  const [paystubsSent, setPaystubsSent] = useState({});  // driver_id → "sent" | "failed"
  const [sendingBatch, setSendingBatch] = useState(false);

  // Local per-row edit state — merges with server data
  const [edits, setEdits] = useState({});

  useEffect(() => { localStorage.setItem("fai_pay_day", payDay); }, [payDay]);

  const linesWithEdits = useMemo(() => {
    if (!data) return [];
    return data.lines.map((line) => {
      const e = edits[line.driver.id];
      if (!e) return line;
      const mergedDeductions = { ...line.deductions, ...e };
      const total = Object.entries(mergedDeductions).reduce(
        (sum, [k, v]) => k === "other_note" ? sum : sum + (Number(v) || 0), 0
      );
      return {
        ...line,
        deductions: { ...line.deductions, ...Object.fromEntries(
          Object.entries(e).filter(([k]) => k !== "other_note")
        )},
        other_note: e.other_note !== undefined ? e.other_note : line.other_note,
        deductions_total: Math.round(total * 100) / 100,
        net_pay: Math.round((line.gross_pay - total) * 100) / 100,
      };
    });
  }, [data, edits]);

  const totals = useMemo(() => {
    if (!linesWithEdits.length) return data?.totals || {};
    const revenue = linesWithEdits.reduce((s, l) => s + l.revenue, 0);
    const driver_gross_pay_total = linesWithEdits.reduce((s, l) => s + l.gross_pay, 0);
    const driver_net_pay_total = linesWithEdits.reduce((s, l) => s + l.net_pay, 0);
    const deductions_total = linesWithEdits.reduce((s, l) => s + l.deductions_total, 0);
    const company_fuel_cost = linesWithEdits.reduce(
      (s, l) => s + (l.driver.employment_type === "company" ? l.fuel_cost : 0), 0
    );
    const owner_net = revenue - driver_gross_pay_total - company_fuel_cost;
    const cash_to_owner = owner_net + deductions_total;
    return {
      revenue, driver_gross_pay_total, driver_net_pay_total, deductions_total,
      company_fuel_cost, owner_net, cash_to_owner,
      load_count: data?.totals?.load_count || 0,
      driver_count: linesWithEdits.filter((l) => l.load_count > 0 || l.gross_pay > 0).length,
    };
  }, [linesWithEdits, data]);

  const load = async () => {
    // Skip if period is invalid (mid-edit) — avoids toast spam while user types
    if (!periodStart || !periodEnd || periodEnd < periodStart) return;
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        client.get(`/payroll/period?period_start=${periodStart}&period_end=${periodEnd}`),
        client.get(`/payroll/paystubs/sent?period_start=${periodStart}&period_end=${periodEnd}`),
      ]);
      setData(pRes.data);
      setEdits({});
      // Prefer 'sent' status if it EVER succeeded. Newer failed retries shouldn't
      // clear an earlier successful send from the UI.
      const sent = {};
      (sRes.data?.sent || []).forEach((s) => {
        if (sent[s.driver_id] === "sent") return;   // already marked sent — don't overwrite
        if (s.status === "sent") sent[s.driver_id] = "sent";
        else if (!sent[s.driver_id]) sent[s.driver_id] = s.status;
      });
      setPaystubsSent(sent);
    } catch (e) {
      if (e?.response?.status !== 400) {
        toast.error(e?.response?.data?.detail || "Could not load payroll");
      }
    } finally { setLoading(false); }
  };

  const previewPaystub = async (driverId) => {
    try {
      const res = await client.get(
        `/payroll/paystub/preview.pdf?period_start=${periodStart}&period_end=${periodEnd}&driver_id=${driverId}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Delay revoking so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const sendPaystub = async (driverId) => {
    try {
      const { data } = await client.post("/payroll/paystub/send", {
        period_start: periodStart,
        period_end: periodEnd,
        driver_ids: [driverId],
      });
      const r = (data?.results || [])[0];
      if (r?.status === "sent") {
        const via = r.provider === "gmail_smtp" ? " (via your Gmail)" :
                    r.provider === "resend" ? " (via Resend)" : "";
        toast.success(`Paystub sent to ${r.driver_email}${via}`);
        setPaystubsSent((prev) => ({ ...prev, [driverId]: "sent" }));
      } else if (r?.status === "skipped") {
        toast.warning(r.error || "Skipped");
      } else {
        toast.error(r?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const sendAllPaystubs = async () => {
    const active = linesWithEdits.filter((l) => l.load_count > 0 || (l.driver.pay_method === "salary" && l.gross_pay > 0));
    if (!active.length) {
      toast.warning("No drivers with pay this period");
      return;
    }
    if (!window.confirm(`Send paystub PDFs to all ${active.length} drivers with pay this period?`)) return;
    setSendingBatch(true);
    try {
      const { data } = await client.post("/payroll/paystub/send", {
        period_start: periodStart,
        period_end: periodEnd,
      });
      toast.success(`Paystubs: sent ${data.sent}, failed ${data.failed}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      // Refresh sent status (same 'sent wins' rule)
      const sRes = await client.get(`/payroll/paystubs/sent?period_start=${periodStart}&period_end=${periodEnd}`);
      const sent = {};
      (sRes.data?.sent || []).forEach((s) => {
        if (sent[s.driver_id] === "sent") return;
        if (s.status === "sent") sent[s.driver_id] = "sent";
        else if (!sent[s.driver_id]) sent[s.driver_id] = s.status;
      });
      setPaystubsSent(sent);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch send failed");
    } finally { setSendingBatch(false); }
  };

  // Debounce period changes — wait 400ms after last edit before hitting API
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [periodStart, periodEnd]);

  const updateDeduction = (driverId, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [driverId]: { ...(prev[driverId] || {}), [key]: value },
    }));
  };

  const saveAdjustments = async (line) => {
    const e = edits[line.driver.id] || {};
    const payload = {
      driver_id: line.driver.id,
      period_start: periodStart,
      period_end: periodEnd,
      fuel_advances: e.fuel_advances ?? line.deductions.fuel_advances ?? 0,
      insurance: e.insurance ?? line.deductions.insurance ?? 0,
      escrow: e.escrow ?? line.deductions.escrow ?? 0,
      chargebacks: e.chargebacks ?? line.deductions.chargebacks ?? 0,
      truck_lease: e.truck_lease ?? line.deductions.truck_lease ?? 0,
      trailer_rental: e.trailer_rental ?? line.deductions.trailer_rental ?? 0,
      other: e.other ?? line.deductions.other ?? 0,
      other_note: e.other_note ?? line.other_note ?? "",
      notes: line.notes || "",
    };
    await client.post("/payroll/adjustments", payload);
    // Clear this driver's edit state (server now has it saved) + reload
    setEdits((prev) => { const copy = { ...prev }; delete copy[line.driver.id]; return copy; });
    await load();
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const response = await client.get(
        `/payroll/export.xlsx?period_start=${periodStart}&period_end=${periodEnd}`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_${periodStart}_to_${periodEnd}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payroll Excel downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const shiftWeek = (weeks) => {
    const s = new Date(periodStart + "T00:00:00Z");
    const e = new Date(periodEnd + "T00:00:00Z");
    s.setDate(s.getDate() + weeks * 7);
    e.setDate(e.getDate() + weeks * 7);
    setPeriodStart(iso(s));
    setPeriodEnd(iso(e));
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="payroll-page">
      <PageHeader
        title="Weekly Payroll"
        eyebrow="Auto-calculated from delivered loads"
        subtitle="Owner-op %, company driver per-mile, or salary — with deductions & Excel export."
      />

      <PayrollQboSyncBar periodStart={periodStart} periodEnd={periodEnd} />

      {/* Period picker */}
      <div className="card-surface p-4 mb-6" data-testid="payroll-period-picker">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => shiftWeek(-1)}
            data-testid="payroll-prev-week"
            className="rounded-full p-2 border border-white/10 hover:border-white/25 transition-colors"
          >
            <ArrowLeft size={14} />
          </button>
          <div className="flex items-center gap-2 text-sm text-neutral-300">
            <Calendar size={16} className="text-[#FF5A1F]" />
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              data-testid="payroll-period-start"
              className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1 text-sm"
            />
            <span className="text-neutral-500">→</span>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              data-testid="payroll-period-end"
              className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1 text-sm"
            />
          </div>
          <button
            onClick={() => shiftWeek(1)}
            data-testid="payroll-next-week"
            className="rounded-full p-2 border border-white/10 hover:border-white/25 transition-colors"
          >
            <ArrowRight size={14} />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="text-xs text-neutral-500">Pay day</div>
            <select
              value={payDay}
              onChange={(e) => setPayDay(e.target.value)}
              data-testid="payroll-pay-day"
              className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1 text-xs text-neutral-300"
            >
              <option value="friday">Friday</option>
              <option value="monday">Monday</option>
            </select>
            <button
              onClick={load}
              disabled={loading}
              data-testid="payroll-refresh"
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-300 hover:text-white hover:border-white/25 inline-flex items-center gap-1 disabled:opacity-60"
            >
              <ArrowClockwise size={12} /> Refresh
            </button>
            <button
              onClick={exportXlsx}
              disabled={exporting || !data || linesWithEdits.length === 0}
              data-testid="payroll-export-xlsx"
              className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <DownloadSimple size={12} weight="fill" /> {exporting ? "Exporting..." : "Download Excel"}
            </button>
            <button
              onClick={sendAllPaystubs}
              disabled={sendingBatch || !data || linesWithEdits.length === 0}
              data-testid="payroll-send-all-paystubs"
              className="rounded-full px-4 py-2 text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors"
            >
              <PaperPlaneTilt size={12} weight="fill" /> {sendingBatch ? "Sending..." : "Send All Paystubs"}
            </button>
          </div>
        </div>
      </div>

      {/* Owner P&L cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="card-surface p-4" data-testid="payroll-stat-revenue">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Revenue</div>
          <div className="text-2xl font-display font-semibold mt-1 text-white tabular-nums">
            {money(totals.revenue)}
          </div>
          <div className="text-[11px] text-neutral-600 mt-1">{totals.load_count || 0} loads delivered</div>
        </div>
        <div className="card-surface p-4" data-testid="payroll-stat-driver-pay">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Driver Pay (net)</div>
          <div className="text-2xl font-display font-semibold mt-1 text-blue-400 tabular-nums">
            −{money(totals.driver_net_pay_total)}
          </div>
          <div className="text-[11px] text-neutral-600 mt-1">{totals.driver_count || 0} drivers</div>
        </div>
        <div className="card-surface p-4" data-testid="payroll-stat-fuel">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Company Fuel</div>
          <div className="text-2xl font-display font-semibold mt-1 text-orange-400 tabular-nums">
            −{money(totals.company_fuel_cost)}
          </div>
          <div className="text-[11px] text-neutral-600 mt-1">Company driver loads only</div>
        </div>
        <div className="card-surface p-4 border-[#FF5A1F]/30" data-testid="payroll-stat-owner-net">
          <div className="text-[10px] uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center gap-1">
            <Wallet size={11} weight="fill" /> Cash to Owner
          </div>
          <div className={`text-2xl font-display font-semibold mt-1 tabular-nums ${totals.cash_to_owner >= 0 ? "text-[#FF5A1F]" : "text-red-500"}`}>
            {money(totals.cash_to_owner)}
          </div>
          <div className="text-[11px] text-neutral-600 mt-1">
            Owner P&L: <span className={totals.owner_net >= 0 ? "text-emerald-400" : "text-red-400"}>{money(totals.owner_net)}</span> + deductions held
          </div>
        </div>
      </div>

      {/* Driver rows */}
      <div className="card-surface overflow-hidden" data-testid="payroll-list">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-neutral-500 border-b border-white/10 bg-white/[0.02]">
          <div className="col-span-4">Driver</div>
          <div className="col-span-1 text-right">Loads</div>
          <div className="col-span-2 text-right">Miles</div>
          <div className="col-span-2 text-right">Revenue</div>
          <div className="col-span-1 text-right">Gross</div>
          <div className="col-span-1 text-right">Deductions</div>
          <div className="col-span-1 text-right">Net Pay</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-neutral-500">Loading payroll…</div>
        ) : linesWithEdits.length === 0 ? (
          <div className="p-12 text-center">
            <Truck size={40} className="mx-auto text-neutral-600 mb-3" />
            <div className="text-white font-medium mb-1">No drivers set up yet</div>
            <div className="text-sm text-neutral-500 mb-4">
              Add drivers on the Drivers page, then set each one's pay config (pay method + rate + default deductions).
            </div>
            <a href="/app/drivers" data-testid="payroll-empty-drivers-link" className="text-xs text-[#FF5A1F] hover:underline">Go to Drivers →</a>
          </div>
        ) : (
          linesWithEdits.map((line) => (
            <DriverRow
              key={line.driver.id}
              line={line}
              expanded={!!expanded[line.driver.id]}
              onToggle={() => setExpanded((prev) => ({ ...prev, [line.driver.id]: !prev[line.driver.id] }))}
              onUpdateDeduction={updateDeduction}
              onSaveAdjustments={saveAdjustments}
              paystubStatus={paystubsSent[line.driver.id]}
              onPreviewPaystub={previewPaystub}
              onSendPaystub={sendPaystub}
            />
          ))
        )}
      </div>

      {/* Footer helper */}
      <div className="mt-6 text-xs text-neutral-500">
        <div className="mb-1">
          <strong className="text-neutral-400">How pay is calculated:</strong>
        </div>
        <ul className="list-disc list-inside space-y-1">
          <li>Only loads with <span className="text-neutral-300">status = "delivered"</span> in this period count.</li>
          <li><span className="text-purple-400">Owner Op</span> drivers pay their own fuel (not deducted from Owner P&L).</li>
          <li>Deductions are the company's money — added back to Cash to Owner.</li>
          <li>Set per-driver pay method and default weekly deductions on the <a href="/app/drivers" className="text-[#FF5A1F] hover:underline">Drivers page</a>.</li>
        </ul>
      </div>
    </div>
  );
}
